import { useState, useEffect } from 'react';
import { Search, Filter, MapPin, Clock, Truck, Package, ChevronDown, AlertTriangle, Navigation, Satellite, ExternalLink, FileText, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getTrucksFromAllProLogsConnections } from '../../services/eldConnectionsService';
import { calculateDistance, calculateETA, formatETA, approximateGeocode } from '../../utils/gpsEta';
import { calculateTimeUntilDelivery, formatTimeRemaining } from '../../utils/timezone';
import { batchCheckLateLoads } from '../../utils/lateLoadTracking';
import { reverseGeocode } from '../../utils/reverseGeocode';

interface LoadTracking {
  id: string;
  loadId: string;
  dispatchNumber: string;
  confirmationNumber: string;
  driver: string;
  truck: string;
  truckNumber: string;
  dispatcher: string;
  dispatchStatus: string;
  origin: string;
  originAddress: string;
  destination: string;
  destinationAddress: string;
  pickupTime: string;
  deliveryTime: string;
  gpsLocation: string;
  gpsCoordinates: string;
  distanceToDestination: number;
  loadedMiles: number;
  emptyMiles: number;
  totalMiles: number;
  timeLeft: string;
  timeNeeded: string;
  status: 'on-time' | 'at-risk' | 'overdue';
  isLoaded: boolean;
  notes: Array<{
    text: string;
    author: string;
    timestamp: string;
  }>;
  detentionTimer?: {
    duration: string;
    arrivedAt: string;
  };
}

interface LateLoad {
  id: string;
  load_id: string;
  load_number: string;
  dispatch_id: string;
  dispatch_number: string;
  driver_id: string;
  driver_name: string;
  scheduled_delivery: string;
  actual_delivery: string;
  hours_late: number;
  reason: string | null;
  created_at: string;
}

interface DetentionClaim {
  id: string;
  load_id: string;
  dispatch_number: string;
  driver_id: string;
  driver_name: string;
  truck_number: string;
  location_type: 'pickup' | 'delivery';
  location_address: string;
  arrival_time: string;
  departure_time: string | null;
  detention_hours: number;
  detention_rate: number;
  total_claim_amount: number;
  claim_status: 'pending' | 'filed' | 'approved' | 'paid' | 'denied';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export function TrackingView() {
  const { profile } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [loads, setLoads] = useState<LoadTracking[]>([]);
  const [lateLoads, setLateLoads] = useState<LateLoad[]>([]);
  const [detentionClaims, setDetentionClaims] = useState<DetentionClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'overdue' | 'at-risk' | 'on-time'>('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeTab, setActiveTab] = useState<'tracking' | 'late-loads' | 'detention'>('tracking');
  const [alertFilter, setAlertFilter] = useState<'all' | 'overdue' | 'at-risk' | 'pickups' | 'deliveries'>('all');
  
  // Check if user can delete late loads (admin or super_admin only)
  const userRole = profile?.role?.toLowerCase();
  const canDeleteLateLoads = userRole === 'admin' || userRole === 'super_admin' || userRole === 'superadmin';
  console.log('[Tracking] Profile role:', profile?.role, 'Can delete late loads:', canDeleteLateLoads);

  const handleDeleteLateLoad = async (lateLoadId: string, dispatchNumber: string) => {
    if (!canDeleteLateLoads) {
      alert('You do not have permission to delete late load records.');
      return;
    }

    if (!confirm(`Are you sure you want to delete late load record for ${dispatchNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('late_loads')
        .delete()
        .eq('id', lateLoadId);

      if (error) throw error;

      // Refresh late loads list
      setLateLoads(lateLoads.filter(ll => ll.id !== lateLoadId));
      console.log(`Late load record deleted: ${dispatchNumber}`);
    } catch (error) {
      console.error('Error deleting late load:', error);
      alert('Failed to delete late load record. Please try again.');
    }
  };

  useEffect(() => {
    loadTrackingData();
  }, []);

  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFilterMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.filter-menu-container')) {
          setShowFilterMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterMenu]);

  // Progressive geocoding - geocode locations one by one and update UI
  async function geocodeLocationsProgressively(truckLocations: any[], trackingLoads: LoadTracking[]) {
    for (const loc of truckLocations) {
      if (!loc.lat || !loc.lng) continue;
      
      try {
        const result = await reverseGeocode(loc.lat, loc.lng);
        
        // Update the truck location with geocoded data
        loc.locationCity = result.city;
        loc.locationState = result.state;
        loc.locationFull = result.fullLocation;
        
        const coordsKey = `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}`;
        
        // Use functional setState to build on previous state
        setLoads(prevLoads => 
          prevLoads.map(load => {
            // Check if this load's GPS matches this location
            if (load.gpsCoordinates === coordsKey) {
              return {
                ...load,
                gpsLocation: result.fullLocation
              };
            }
            return load;
          })
        );
        
        console.log(`[Geocode] Updated ${result.fullLocation} for ${coordsKey}`);
      } catch (error) {
        console.error('Progressive geocoding error:', error);
      }
    }
    console.log('[Tracking] Progressive geocoding complete');
  }

  async function loadTrackingData() {
    try {
      setLoading(true);
      
      // Fetch active dispatches with all related data in one query
      const { data: loadsData, error } = await supabase
        .from('dispatches')
        .select(`
          *,
          loads!dispatches_load_id_fkey (
            id,
            load_number,
            pickup_city,
            pickup_state,
            delivery_city,
            delivery_state,
            total_miles,
            load_stops(stop_type, stop_order, city, state, date, time_from, time_to)
          ),
          drivers!dispatches_driver_id_fkey (
            first_name,
            last_name
          ),
          trucks!dispatches_truck_id_fkey (
            unit_number,
            samsara_id,
            samsara_vehicle_id,
            dispatcher_id
          )
        `)
        .in('status', ['active', 'dispatched', 'loaded']);
      
      // Fetch dispatcher profiles to get names
      const dispatcherIds = loadsData?.map((d: any) => d.trucks?.dispatcher_id).filter(Boolean) || [];
      let dispatcherMap = new Map();
      
      if (dispatcherIds.length > 0) {
        const { data: dispatcherData } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', dispatcherIds);
        
        if (dispatcherData) {
          dispatcherData.forEach((dispatcher: any) => {
            dispatcherMap.set(dispatcher.id, dispatcher.full_name);
          });
        }
      }
      
      // Fetch load notes separately for all loads
      const loadIds = loadsData?.map((d: any) => d.loads?.id).filter(Boolean) || [];
      let notesMap = new Map();
      
      console.log('Tracking: Fetching notes for', loadIds.length, 'loads');
      
      if (loadIds.length > 0) {
        const { data: notesData, error: notesError } = await supabase
          .from('load_notes')
          .select('*, profiles!load_notes_created_by_fkey(full_name)')
          .in('load_id', loadIds)
          .order('created_at', { ascending: false });
        
        if (notesError) {
          console.error('Tracking: Error fetching notes:', notesError);
        } else {
          console.log('Tracking: Fetched', notesData?.length || 0, 'notes');
        }
        
        // Group notes by load_id
        if (notesData) {
          notesData.forEach((note: any) => {
            if (!notesMap.has(note.load_id)) {
              notesMap.set(note.load_id, []);
            }
            notesMap.get(note.load_id).push(note);
          });
          console.log('Tracking: Notes grouped for', notesMap.size, 'loads');
        }
      }

      if (error) throw error;

      // Fetch late loads history
      const { data: lateLoadsData, error: lateLoadsError } = await supabase
        .from('late_loads')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (lateLoadsError) {
        console.error('Error loading late loads:', lateLoadsError);
        setLateLoads([]);
      } else {
        setLateLoads(lateLoadsData || []);
      }

      // Fetch detention claims
      const { data: detentionClaimsData, error: detentionClaimsError } = await supabase
        .from('detention_claims')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (detentionClaimsError) {
        console.error('Error loading detention claims:', detentionClaimsError);
        setDetentionClaims([]);
      } else {
        setDetentionClaims(detentionClaimsData || []);
      }

      // Fetch truck locations from ELD (ProLogs)
      let truckLocations: any[] = [];
      try {
        const eldResult = await getTrucksFromAllProLogsConnections();
        truckLocations = eldResult.success && eldResult.trucks ? eldResult.trucks : [];
        console.log('[Tracking] ELD trucks fetched:', truckLocations.length);
      } catch (error) {
        console.error('ELD API error:', error);
        // Continue without GPS data
      }

      // Add locationCity/State placeholders for progressive loading
      truckLocations = truckLocations.map(loc => ({
        ...loc,
        locationCity: null,
        locationState: null,
        locationFull: null
      }));

      // Transform database data to tracking format with GPS-based ETA
      const trackingLoads: LoadTracking[] = (loadsData || [])
        .filter((dispatch: any) => {
          // Filter out removed loads
          const load = dispatch.loads;
          if (!load) return false;
          const loadNumber = load.load_number || '';
          if (loadNumber.includes('_REMOVED_')) {
            console.log('Tracking: Filtering out removed load:', loadNumber);
            return false;
          }
          return true;
        })
        .map((dispatch: any) => {
        const load = dispatch.loads;
        
        const driverName = dispatch.drivers 
          ? `${dispatch.drivers.first_name} ${dispatch.drivers.last_name}`
          : 'Unassigned';
        const truckNumber = dispatch.trucks?.unit_number || 'N/A';
        const dispatcherId = dispatch.trucks?.dispatcher_id;
        const dispatcher = dispatcherId ? (dispatcherMap.get(dispatcherId) || 'N/A') : 'N/A';
        
        // Extract pickup and delivery from load_stops if needed
        let pickupCity = load?.pickup_city;
        let pickupState = load?.pickup_state;
        let deliveryCity = load?.delivery_city;
        let deliveryState = load?.delivery_state;
        let pickupDateTime = null;
        let deliveryDateTime = null;
        
        if (load?.load_stops && load.load_stops.length > 0) {
          const sortedStops = [...load.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);
          
          // Get first shipper stop for pickup
          const pickupStop = sortedStops.find((s: any) => s.stop_type === 'shipper') || sortedStops[0];
          if (pickupStop) {
            if (!pickupCity || pickupCity === 'TBD') pickupCity = pickupStop.city;
            if (!pickupState || pickupState === 'TBD') pickupState = pickupStop.state;
            // Get pickup date and time
            if (pickupStop.date) {
              const dateStr = pickupStop.date;
              const timeStr = pickupStop.time_from || '00:00:00';
              pickupDateTime = new Date(`${dateStr}T${timeStr}`);
            }
          }
          
          // Get last stop for delivery
          const lastStop = sortedStops[sortedStops.length - 1];
          if (lastStop) {
            if (!deliveryCity || deliveryCity === 'TBD') deliveryCity = lastStop.city;
            if (!deliveryState || deliveryState === 'TBD') deliveryState = lastStop.state;
            // Get delivery date and time
            if (lastStop.date) {
              const dateStr = lastStop.date;
              const timeStr = lastStop.time_to || lastStop.time_from || '23:59:59';
              deliveryDateTime = new Date(`${dateStr}T${timeStr}`);
            }
          }
        }
        
        const origin = pickupCity && pickupState ? `${pickupCity}, ${pickupState}` : 'Unknown';
        const destination = deliveryCity && deliveryState ? `${deliveryCity}, ${deliveryState}` : 'Unknown';
        
        // Get truck's current location from ELD (if available)
        const truckUnitNumber = dispatch.trucks?.unit_number;
        // Match by unit number - ELD data uses truck number without T- prefix
        const truckLocation = truckLocations.find((loc: any) => {
          const eldName = loc.name || '';
          const unitNum = truckUnitNumber || '';
          // Try exact match first
          if (eldName === unitNum) return true;
          // Try without T- prefix
          if (eldName === unitNum.replace('T-', '')) return true;
          // Try with T- prefix added to ELD name
          if (`T-${eldName}` === unitNum) return true;
          return false;
        });
        
        let gpsCoords = 'GPS Unavailable';
        let distanceToDestination = load?.total_miles || 0;
        let timeLeft = '';
        let timeNeeded = '';
        let status: 'on-time' | 'at-risk' | 'overdue' = 'on-time';
        
        if (truckLocation && truckLocation.lat && truckLocation.lng) {
          const lat = truckLocation.lat || 0;
          const lng = truckLocation.lng || 0;
          
          // Show GPS coordinates
          gpsCoords = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
          
          // Try to geocode destination and calculate GPS-based ETA
          const deliveryCity = load?.delivery_city;
          const deliveryState = load?.delivery_state;
          
          if (deliveryCity && deliveryState) {
            const deliveryCoords = approximateGeocode(deliveryCity, deliveryState);
            
            if (deliveryCoords) {
              // Calculate real distance from current GPS location
              distanceToDestination = calculateDistance(
                lat,
                lng,
                deliveryCoords.lat,
                deliveryCoords.lng
              );
              
              // Calculate ETA at 55mph
              const etaHours = calculateETA(distanceToDestination, 55);
              timeNeeded = `${formatETA(etaHours)} (${distanceToDestination}mi)`;
              
              // Calculate time remaining based on delivery location timezone
              // Use actual delivery time from load_stops if available
              const targetDelivery = deliveryDateTime || 
                (dispatch.dispatch_date ? new Date(new Date(dispatch.dispatch_date).getTime() + (24 * 60 * 60 * 1000)) : new Date());
              
              const timeUntilDelivery = calculateTimeUntilDelivery(
                targetDelivery.toISOString(),
                deliveryState
              );
              
              const hoursUntilDelivery = timeUntilDelivery.totalMinutes / 60;
              
              if (timeUntilDelivery.isOverdue) {
                status = 'overdue';
                timeLeft = formatTimeRemaining(timeUntilDelivery.hours, timeUntilDelivery.minutes, true);
              } else if (etaHours > hoursUntilDelivery) {
                // If ETA exceeds delivery time by more than 1 hour, it's overdue (won't make it)
                // Only mark as at-risk if within 1 hour buffer
                const hoursDifference = etaHours - hoursUntilDelivery;
                if (hoursDifference > 1) {
                  status = 'overdue';
                  timeLeft = formatTimeRemaining(timeUntilDelivery.hours, timeUntilDelivery.minutes, false);
                } else {
                  status = 'at-risk';
                  timeLeft = formatTimeRemaining(timeUntilDelivery.hours, timeUntilDelivery.minutes, false);
                }
              } else {
                status = 'on-time';
                timeLeft = formatTimeRemaining(timeUntilDelivery.hours, timeUntilDelivery.minutes, false);
              }
            }
          }
        }
        
        // Fallback to simple calculation if GPS not available
        const loadedMiles = load?.total_miles || 0;
        const emptyMiles = dispatch?.empty_miles || 0;
        
        if (!timeNeeded) {
          distanceToDestination = loadedMiles;
          const hoursNeeded = loadedMiles / 50;
          timeNeeded = `${Math.round(hoursNeeded)}h ${Math.round((hoursNeeded % 1) * 60)}m @ 50mph`;
          
          // Calculate time remaining based on delivery location timezone
          // Use actual delivery time from load_stops if available
          const targetDelivery = deliveryDateTime || 
            (dispatch.dispatch_date ? new Date(new Date(dispatch.dispatch_date).getTime() + (24 * 60 * 60 * 1000)) : new Date());
          
          const timeUntilDelivery = calculateTimeUntilDelivery(
            targetDelivery.toISOString(),
            deliveryState
          );
          
          const hoursUntilDelivery = timeUntilDelivery.totalMinutes / 60;
          
          if (timeUntilDelivery.isOverdue) {
            status = 'overdue';
            timeLeft = formatTimeRemaining(timeUntilDelivery.hours, timeUntilDelivery.minutes, true);
          } else if (hoursNeeded > hoursUntilDelivery) {
            // If hours needed exceeds time until delivery by more than 1 hour, it's overdue (won't make it)
            // Only mark as at-risk if within 1 hour buffer
            const hoursDifference = hoursNeeded - hoursUntilDelivery;
            if (hoursDifference > 1) {
              status = 'overdue';
              timeLeft = formatTimeRemaining(timeUntilDelivery.hours, timeUntilDelivery.minutes, false);
            } else {
              status = 'at-risk';
              timeLeft = formatTimeRemaining(timeUntilDelivery.hours, timeUntilDelivery.minutes, false);
            }
          } else {
            status = 'on-time';
            timeLeft = formatTimeRemaining(timeUntilDelivery.hours, timeUntilDelivery.minutes, false);
          }
        }

        return {
          id: dispatch.id,
          loadId: load?.id || dispatch.id,
          dispatchNumber: dispatch.dispatch_number,
          confirmationNumber: load?.load_number || 'N/A',
          driver: driverName,
          truck: truckNumber,
          truckNumber: truckNumber.replace('T-', ''),
          dispatcher: dispatcher,
          dispatchStatus: dispatch.status || 'active',
          origin: origin || 'Unknown',
          originAddress: '',
          destination: destination,
          destinationAddress: '',
          pickupTime: pickupDateTime 
            ? pickupDateTime.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                hour: 'numeric', 
                minute: '2-digit' 
              })
            : (dispatch.dispatch_date 
                ? new Date(dispatch.dispatch_date).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    hour: 'numeric', 
                    minute: '2-digit' 
                  })
                : 'TBD'),
          deliveryTime: deliveryDateTime
            ? deliveryDateTime.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                hour: 'numeric', 
                minute: '2-digit' 
              })
            : (dispatch.dispatch_date
                ? new Date(new Date(dispatch.dispatch_date).getTime() + (24 * 60 * 60 * 1000)).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    hour: 'numeric', 
                    minute: '2-digit' 
                  })
                : 'TBD'),
          gpsLocation: gpsCoords,
          gpsCoordinates: gpsCoords,
          distanceToDestination: distanceToDestination,
          loadedMiles,
          emptyMiles: emptyMiles,
          totalMiles: loadedMiles + emptyMiles,
          timeLeft,
          timeNeeded,
          status,
          isLoaded: dispatch.is_loaded || false,
          notes: (notesMap.get(load?.id) || []).map((note: any) => {
            console.log('Tracking: Adding note for load', load?.id, ':', note);
            return {
              text: note.note || note.note_text || note.content || '',
              author: note.profiles?.full_name || 'System',
              timestamp: new Date(note.created_at).toLocaleString()
            };
          }),
          detentionTimer: dispatch.arrived_at_delivery ? {
            duration: dispatch.delivery_detention_start 
              ? `${Math.round((now.getTime() - new Date(dispatch.delivery_detention_start).getTime()) / (1000 * 60 * 60))}h`
              : '0h',
            arrivedAt: new Date(dispatch.arrived_at_delivery).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
          } : undefined
        };
      });

      // Set loads immediately with coordinates
      setLoads(trackingLoads);

      // Start progressive geocoding in background
      if (truckLocations.length > 0) {
        console.log('[Tracking] Starting progressive geocoding...');
        geocodeLocationsProgressively(truckLocations, trackingLoads);
      }

      // Automatically check and record late loads
      // Build checks for loads with GPS location
      const lateLoadChecks = loadsData
        ?.filter((dispatch: any) => {
          const truckUnitNumber = dispatch.trucks?.unit_number;
          const truckLocation = truckLocations.find((loc: any) => {
            const eldName = loc.name || '';
            const unitNum = truckUnitNumber || '';
            if (eldName === unitNum) return true;
            if (eldName === unitNum.replace('T-', '')) return true;
            if (`T-${eldName}` === unitNum) return true;
            return false;
          });
          return truckLocation && truckLocation.lat && truckLocation.lng && dispatch.loads;
        })
        .map((dispatch: any) => {
          const truckUnitNumber = dispatch.trucks?.unit_number;
          const truckLocation = truckLocations.find((loc: any) => {
            const eldName = loc.name || '';
            const unitNum = truckUnitNumber || '';
            if (eldName === unitNum) return true;
            if (eldName === unitNum.replace('T-', '')) return true;
            if (`T-${eldName}` === unitNum) return true;
            return false;
          });
          const load = dispatch.loads;
          
          // Get delivery location from load_stops if needed
          let deliveryCity = load.delivery_city;
          let deliveryState = load.delivery_state;
          
          if (load.load_stops && load.load_stops.length > 0) {
            const sortedStops = [...load.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);
            const lastStop = sortedStops[sortedStops.length - 1];
            if (!deliveryCity || deliveryCity === 'TBD') deliveryCity = lastStop.city;
            if (!deliveryState || deliveryState === 'TBD') deliveryState = lastStop.state;
          }
          
          const driverName = dispatch.drivers 
            ? `${dispatch.drivers.first_name} ${dispatch.drivers.last_name}`
            : 'Unassigned';
          
          // Get actual delivery time from load_stops
          let targetDelivery = null;
          if (load.load_stops && load.load_stops.length > 0) {
            const sortedStops = [...load.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);
            const lastStop = sortedStops[sortedStops.length - 1];
            if (lastStop.date) {
              const dateStr = lastStop.date;
              const timeStr = lastStop.time_to || lastStop.time_from || '23:59:59';
              targetDelivery = new Date(`${dateStr}T${timeStr}`);
            }
          }
          
          // Fallback to dispatch_date + 24h if no load_stops
          if (!targetDelivery) {
            const dispatchDate = dispatch.dispatch_date ? new Date(dispatch.dispatch_date) : new Date();
            targetDelivery = new Date(dispatchDate.getTime() + (24 * 60 * 60 * 1000));
          }
          
          return {
            dispatchId: dispatch.id,
            dispatchNumber: dispatch.dispatch_number,
            loadId: load.id,
            loadNumber: load.load_number || 'N/A',
            driverName,
            truckNumber: dispatch.trucks?.unit_number || 'N/A',
            currentLat: truckLocation.lat,
            currentLng: truckLocation.lng,
            deliveryCity,
            deliveryState,
            scheduledDelivery: targetDelivery.toISOString(),
            dispatchStatus: dispatch.status
          };
        }) || [];

      // Check for late loads in background (don't block UI)
      if (lateLoadChecks.length > 0) {
        batchCheckLateLoads(lateLoadChecks).catch(err => {
          console.error('Error checking late loads:', err);
        });
      }
    } catch (error) {
      console.error('Error loading tracking data:', error);
    } finally {
      setLoading(false);
    }
  }

  // Calculate pickups and deliveries in next 2 hours for alert filter
  const now = new Date();
  const twoHoursFromNow = new Date(now.getTime() + (2 * 60 * 60 * 1000));

  // Filter and search logic
  const filteredLoads = loads.filter(load => {
    // Apply status filter
    if (statusFilter !== 'all' && load.status !== statusFilter) {
      return false;
    }

    // Apply alert filter
    if (alertFilter !== 'all') {
      if (alertFilter === 'overdue' && load.status !== 'overdue') {
        return false;
      }
      if (alertFilter === 'at-risk' && load.status !== 'at-risk') {
        return false;
      }
      if (alertFilter === 'pickups') {
        try {
          const pickupTime = new Date(load.pickupTime);
          if (!(pickupTime >= now && pickupTime <= twoHoursFromNow)) {
            return false;
          }
        } catch {
          return false;
        }
      }
      if (alertFilter === 'deliveries') {
        try {
          const deliveryTime = new Date(load.deliveryTime);
          if (!(deliveryTime >= now && deliveryTime <= twoHoursFromNow)) {
            return false;
          }
        } catch {
          return false;
        }
      }
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        load.dispatchNumber.toLowerCase().includes(query) ||
        load.confirmationNumber.toLowerCase().includes(query) ||
        load.driver.toLowerCase().includes(query) ||
        load.truck.toLowerCase().includes(query) ||
        load.origin.toLowerCase().includes(query) ||
        load.destination.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Sort loads by delivery time - earliest deadline first
  const sortedLoads = [...filteredLoads].sort((a, b) => {
    try {
      const dateA = new Date(a.deliveryTime);
      const dateB = new Date(b.deliveryTime);
      return dateA.getTime() - dateB.getTime();
    } catch {
      return 0;
    }
  });

  const overdueLoads = sortedLoads.filter(l => l.status === 'overdue');
  const atRiskLoads = filteredLoads.filter(l => l.status === 'at-risk');
  const onTimeLoads = filteredLoads.filter(l => l.status === 'on-time');
  
  // Calculate pickups and deliveries in next 2 hours (using now and twoHoursFromNow from above)
  const upcomingPickups = filteredLoads.filter(l => {
    try {
      const pickupTime = new Date(l.pickupTime);
      return pickupTime >= now && pickupTime <= twoHoursFromNow;
    } catch {
      return false;
    }
  });
  
  const upcomingDeliveries = filteredLoads.filter(l => {
    try {
      const deliveryTime = new Date(l.deliveryTime);
      return deliveryTime >= now && deliveryTime <= twoHoursFromNow;
    } catch {
      return false;
    }
  });
  
  const vehiclesTracked = filteredLoads.length;
  const inTransit = filteredLoads.length;
  const onTimePercentage = filteredLoads.length > 0 ? Math.round((onTimeLoads.length / filteredLoads.length) * 100) : 0;
  const activeRoutes = filteredLoads.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#AC021A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Tracking</h1>
        <p className="text-slate-600">Real-time fleet and shipment tracking</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('tracking')}
            className={`px-4 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'tracking'
                ? 'border-[#AC021A] text-[#AC021A]'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            Live Tracking
          </button>
          <button
            onClick={() => setActiveTab('late-loads')}
            className={`px-4 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'late-loads'
                ? 'border-[#AC021A] text-[#AC021A]'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            Late Loads History
            {lateLoads.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full">
                {lateLoads.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('detention')}
            className={`px-4 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'detention'
                ? 'border-[#AC021A] text-[#AC021A]'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            Detention Claims
            {detentionClaims.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-600 text-xs font-bold rounded-full">
                {detentionClaims.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'tracking' ? (
        <>
          {/* Alert Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Overdue Card */}
        <div className="rounded-lg border border-red-500 bg-red-500/10 shadow-sm">
          <div className="p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-red-600">{overdueLoads.length} OVERDUE</p>
                <p className="text-xs text-slate-600 line-clamp-2">Passed delivery window</p>
                <button 
                  onClick={() => setAlertFilter(alertFilter === 'overdue' ? 'all' : 'overdue')}
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold transition-colors mt-2 cursor-pointer text-xs h-6 ${
                    alertFilter === 'overdue' 
                      ? 'border-transparent bg-red-700 text-white' 
                      : 'border-transparent bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {alertFilter === 'overdue' ? 'Clear' : 'View'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* At-Risk Card */}
        <div className="rounded-lg border border-yellow-500 bg-yellow-500/10 shadow-sm">
          <div className="p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-yellow-600">{atRiskLoads.length} At-Risk</p>
                <p className="text-xs text-slate-600 line-clamp-2">May not arrive on time</p>
                <button 
                  onClick={() => setAlertFilter(alertFilter === 'at-risk' ? 'all' : 'at-risk')}
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold transition-colors mt-2 cursor-pointer text-xs h-6 ${
                    alertFilter === 'at-risk'
                      ? 'border-yellow-600 bg-yellow-500/30 text-yellow-700'
                      : 'border-yellow-500 text-yellow-600 hover:bg-yellow-500/20'
                  }`}
                >
                  {alertFilter === 'at-risk' ? 'Clear' : 'View'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Pickups Card */}
        <div className="rounded-lg border border-orange-500 bg-orange-500/10 shadow-sm">
          <div className="p-4">
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-orange-600">{upcomingPickups.length} Pickups</p>
                <p className="text-xs text-slate-600 line-clamp-2">Next 2 hours</p>
                <button 
                  onClick={() => setAlertFilter(alertFilter === 'pickups' ? 'all' : 'pickups')}
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold transition-colors mt-2 cursor-pointer text-xs h-6 ${
                    alertFilter === 'pickups'
                      ? 'border-orange-600 bg-orange-500/30 text-orange-700'
                      : 'border-orange-500 text-orange-600 hover:bg-orange-500/20'
                  }`}
                >
                  {alertFilter === 'pickups' ? 'Clear' : 'View'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Deliveries Card */}
        <div className="rounded-lg border border-blue-500 bg-blue-500/10 shadow-sm">
          <div className="p-4">
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-blue-600">{upcomingDeliveries.length} Deliveries</p>
                <p className="text-xs text-slate-600 line-clamp-2">Next 2 hours</p>
                <button 
                  onClick={() => setAlertFilter(alertFilter === 'deliveries' ? 'all' : 'deliveries')}
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold transition-colors mt-2 cursor-pointer text-xs h-6 ${
                    alertFilter === 'deliveries'
                      ? 'border-blue-600 bg-blue-500/30 text-blue-700'
                      : 'border-blue-500 text-blue-600 hover:bg-blue-500/20'
                  }`}
                >
                  {alertFilter === 'deliveries' ? 'Clear' : 'View'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="p-6 space-y-4">
          <div className="flex gap-3 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search by dispatch #, load #, confirmation #, truck, driver, or destination..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="relative filter-menu-container">
              <button 
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <Filter className="h-4 w-4" />
                <span className="text-sm">
                  {statusFilter === 'all' ? 'All Status' : 
                   statusFilter === 'overdue' ? 'Overdue' :
                   statusFilter === 'at-risk' ? 'At Risk' : 'On Time'}
                </span>
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {showFilterMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setShowFilterMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${
                        statusFilter === 'all' ? 'bg-slate-100 font-semibold' : ''
                      }`}
                    >
                      All Status
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('overdue');
                        setShowFilterMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${
                        statusFilter === 'overdue' ? 'bg-slate-100 font-semibold' : ''
                      }`}
                    >
                      🔴 Overdue
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('at-risk');
                        setShowFilterMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${
                        statusFilter === 'at-risk' ? 'bg-slate-100 font-semibold' : ''
                      }`}
                    >
                      🟡 At Risk
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('on-time');
                        setShowFilterMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${
                        statusFilter === 'on-time' ? 'bg-slate-100 font-semibold' : ''
                      }`}
                    >
                      🟢 On Time
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm hover:border-[#AC021A]/50 transition-colors">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Vehicles Tracked</p>
                <p className="text-3xl font-bold text-slate-900">{vehiclesTracked}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-[#AC021A]/10 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-[#AC021A]" />
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm hover:border-[#AC021A]/50 transition-colors">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">In Transit</p>
                <p className="text-3xl font-bold text-slate-900">{inTransit}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-[#AC021A]/10 flex items-center justify-center">
                <Package className="h-6 w-6 text-[#AC021A]" />
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm hover:border-[#AC021A]/50 transition-colors">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">On Time</p>
                <p className="text-3xl font-bold text-slate-900">{onTimePercentage}%</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-[#AC021A]/10 flex items-center justify-center">
                <Clock className="h-6 w-6 text-[#AC021A]" />
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm hover:border-[#AC021A]/50 transition-colors">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Active Routes</p>
                <p className="text-3xl font-bold text-slate-900">{activeRoutes}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-[#AC021A]/10 flex items-center justify-center">
                <Navigation className="h-6 w-6 text-[#AC021A]" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Load Cards Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sortedLoads.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Loads Found</h3>
            <p className="text-slate-600">
              {searchQuery ? 'Try adjusting your search query.' : 'No loads match the selected filters.'}
            </p>
          </div>
        ) : (
          sortedLoads.map((load) => (
          <div key={load.id} className="rounded-lg border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
            {/* Card Header */}
            <div className="flex flex-col space-y-1.5 p-4 pb-3 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <div className="flex items-center justify-between">
                <a 
                  href={`/dispatches/${load.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    window.history.pushState({}, '', `/dispatches/${load.id}`);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  className="font-semibold tracking-tight text-base flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                >
                  <Package className="h-4 w-4" />
                  {load.dispatchNumber}
                </a>
                <div className="flex items-center gap-2">
                  {/* Dispatch Status Badge */}
                  <span className={`inline-flex items-center rounded-full border font-semibold transition-colors text-xs px-2 py-0 ${
                    load.dispatchStatus === 'loaded'
                      ? 'border-transparent bg-green-600 text-white'
                      : load.dispatchStatus === 'dispatched'
                      ? 'border-transparent bg-yellow-500 text-white'
                      : load.dispatchStatus === 'problem'
                      ? 'border-transparent bg-purple-600 text-white'
                      : load.dispatchStatus === 'broken_down'
                      ? 'border-transparent bg-red-600 text-white'
                      : load.dispatchStatus === 'broken_on_load'
                      ? 'border-transparent bg-teal-600 text-white'
                      : load.dispatchStatus === 'has_load'
                      ? 'border-transparent bg-slate-400 text-white'
                      : 'border-transparent bg-blue-600 text-white'
                  }`}>
                    {load.dispatchStatus === 'loaded' ? 'Loaded' 
                      : load.dispatchStatus === 'dispatched' ? 'Dispatched'
                      : load.dispatchStatus === 'problem' ? 'Problem'
                      : load.dispatchStatus === 'broken_down' ? 'Broken Down'
                      : load.dispatchStatus === 'broken_on_load' ? 'Broken on Load'
                      : load.dispatchStatus === 'has_load' ? 'Has Load'
                      : 'Active'}
                  </span>
                  {/* ETA Status Badge */}
                  <span className={`inline-flex items-center rounded-full border font-semibold transition-colors text-xs px-2 py-0 ${
                    load.status === 'overdue'
                      ? 'border-transparent bg-red-600 text-white'
                      : load.status === 'at-risk'
                      ? 'border-transparent bg-orange-600 text-white'
                      : 'border-transparent bg-green-600 text-white'
                  }`}>
                    {load.status === 'overdue' ? '❌ Overdue' : load.status === 'at-risk' ? '⚠️ At Risk' : '✓ On Time'}
                  </span>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                Load ID: 
                <a 
                  href={`/loads/${load.loadId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    window.history.pushState({}, '', `/loads/${load.loadId}`);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  className="ml-1 text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                >
                  {load.confirmationNumber}
                </a>
              </p>
            </div>

            {/* Card Body - Tabbed Interface */}
            <div className="p-4 space-y-3">
              {/* Quick Info Bar */}
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Truck className="h-4 w-4 text-slate-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate">{load.truck}</p>
                    <p className="text-[10px] text-slate-600 truncate">{load.driver}</p>
                  </div>
                </div>
                <a
                  href={`/track/${load.truckNumber}`}
                  onClick={(e) => {
                    e.preventDefault();
                    window.history.pushState({}, '', `/track/${load.truckNumber}`);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors cursor-pointer flex-shrink-0"
                >
                  <Satellite className="h-3 w-3" />
                  Track
                </a>
              </div>

              {/* Route Summary - Compact */}
              <div className="space-y-2">
                {/* Current Location */}
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-100">
                  <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">Current GPS</p>
                    <a
                      href={`https://maps.google.com/?q=${load.gpsCoordinates}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-blue-700 hover:underline cursor-pointer inline-flex items-center gap-1"
                    >
                      {load.gpsLocation}
                      <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  </div>
                </div>

                {/* Route Arrow */}
                <div className="flex items-center justify-center">
                  <Navigation className="h-4 w-4 text-slate-400" />
                </div>

                {/* Pickup & Delivery in Grid */}
                <div className="grid grid-cols-2 gap-2">
                  {/* Pickup */}
                  <div className="p-2 bg-orange-50 rounded border border-orange-200">
                    <div className="flex items-center gap-1 mb-1">
                      <MapPin className="h-3 w-3 text-orange-600" />
                      <p className="text-[10px] font-semibold text-orange-600 uppercase tracking-wide">Pickup</p>
                    </div>
                    <p className="text-xs font-medium text-orange-700 truncate">📍 {load.origin}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3 text-orange-500" />
                      <p className="text-[10px] text-orange-600">{load.pickupTime}</p>
                    </div>
                  </div>

                  {/* Delivery */}
                  <div className="p-2 bg-emerald-50 rounded border border-emerald-200">
                    <div className="flex items-center gap-1 mb-1">
                      <MapPin className="h-3 w-3 text-emerald-600" />
                      <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide">Delivery</p>
                    </div>
                    <p className="text-xs font-medium text-emerald-700 truncate">🎯 {load.destination}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3 text-emerald-500" />
                      <p className="text-[10px] text-emerald-600">{load.deliveryTime}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ETA & Distance - Prominent Display */}
              <div className={`p-3 rounded-lg border-2 ${
                load.status === 'overdue'
                  ? 'bg-red-50 border-red-300'
                  : load.status === 'at-risk'
                  ? 'bg-yellow-50 border-yellow-300'
                  : 'bg-green-50 border-green-300'
              }`}>
                <div className="grid grid-cols-2 gap-3">
                  {/* Time Status */}
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Clock className={`h-4 w-4 ${
                        load.status === 'overdue' ? 'text-red-600' : 
                        load.status === 'at-risk' ? 'text-yellow-600' : 
                        'text-green-600'
                      }`} />
                      <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">Time Status</p>
                    </div>
                    <p className="text-lg font-bold text-slate-900">{load.timeLeft}</p>
                    <p className={`text-xs font-medium mt-1 ${
                      load.status === 'overdue' ? 'text-red-700' : 
                      load.status === 'at-risk' ? 'text-yellow-700' : 
                      'text-green-700'
                    }`}>
                      {load.status === 'overdue' ? '❌' : load.status === 'at-risk' ? '⚠️' : '✅'} {load.timeNeeded}
                    </p>
                  </div>

                  {/* Distance */}
                  <div className="text-center border-l-2 border-slate-200 pl-3">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Navigation className="h-4 w-4 text-slate-600" />
                      <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">Distance</p>
                    </div>
                    <p className="text-lg font-bold text-slate-900">{load.distanceToDestination} mi</p>
                    <p className="text-xs text-slate-600 mt-1">
                      {load.dispatchStatus === 'loaded' ? 'To Delivery' : 'To Pickup'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              {load.notes.length > 0 && (
                <>
                  <div className="shrink-0 bg-slate-200 h-[1px] w-full my-1.5"></div>
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded p-2">
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-blue-600 mb-1">📝 Load Notes</p>
                        <div className="space-y-1.5 max-h-60 overflow-y-auto">
                          {load.notes.map((note, idx) => (
                            <div key={idx} className={`text-xs p-2 rounded ${
                              idx === 0 ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-slate-100'
                            }`}>
                              <p className="text-slate-900 font-medium">{note.text}</p>
                              <p className="text-[10px] text-slate-600 mt-0.5">{note.author} • {note.timestamp}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Detention Timer */}
              {load.detentionTimer && (
                <div className="bg-orange-500/10 border border-orange-500/20 rounded p-2 mt-2">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-orange-600">🚚 Driver at Delivery - Detention Timer</p>
                      <p className="text-sm font-bold text-slate-900 mt-0.5">{load.detentionTimer.duration}</p>
                      <p className="text-[10px] text-slate-600">Arrived: {load.detentionTimer.arrivedAt}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Load Details - Miles Breakdown */}
              <div className="grid grid-cols-3 gap-2 p-2 bg-slate-100 rounded border border-slate-200">
                <div className="text-center">
                  <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">Total Miles</p>
                  <p className="text-base font-bold text-slate-900">{load.totalMiles}</p>
                  <p className="text-[9px] text-slate-500">Full Load</p>
                </div>
                <div className="text-center border-l border-r border-slate-300">
                  <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">Empty Miles</p>
                  <p className="text-base font-bold text-amber-700">{load.emptyMiles || 0}</p>
                  <p className="text-[9px] text-slate-500">Deadhead</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">Loaded Miles</p>
                  <p className="text-base font-bold text-blue-700">{load.loadedMiles}</p>
                  <p className="text-[9px] text-slate-500">Revenue</p>
                </div>
              </div>
              
              {/* Dispatcher Info */}
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                <div className="flex-1">
                  <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">Dispatcher</p>
                  <p className="text-xs font-medium text-slate-700">{load.dispatcher}</p>
                </div>
              </div>
            </div>
          </div>
          ))
        )}
      </div>
        </>
      ) : activeTab === 'late-loads' ? (
        /* Late Loads History Tab */
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Explanation */}
          <div className="bg-blue-50 border-b border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 mb-1">How Late Loads Are Tracked</h3>
                <p className="text-xs text-blue-800">
                  Loads are automatically recorded here when they are <strong>more than 2 hours late</strong> past their scheduled delivery deadline. 
                  The system tracks hours late continuously until the dispatch is marked as delivered/completed. 
                  All times are calculated based on the <strong>delivery location's timezone</strong> for accuracy.
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Load ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Dispatch ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Driver Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Scheduled Delivery
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actual Delivery
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Hours Late
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Recorded Date
                  </th>
                  {canDeleteLateLoads && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {lateLoads.length === 0 ? (
                  <tr>
                    <td colSpan={canDeleteLateLoads ? 9 : 8} className="px-6 py-12 text-center text-slate-500">
                      <div className="flex flex-col items-center">
                        <Package className="w-12 h-12 text-slate-300 mb-3" />
                        <p className="font-semibold text-slate-700">No Late Loads Recorded</p>
                        <p className="text-sm text-slate-500 mt-1">All deliveries have been on time! 🎉</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  lateLoads.map((lateLoad) => (
                    <tr key={lateLoad.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <a 
                          href={`/loads/${lateLoad.load_id}`}
                          className="font-semibold text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {lateLoad.load_number || 'N/A'}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <a 
                          href={`/dispatches/${lateLoad.dispatch_id}`}
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {lateLoad.dispatch_number}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-900">{lateLoad.driver_name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-900">
                          {new Date(lateLoad.scheduled_delivery).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-900">
                          {lateLoad.actual_delivery === lateLoad.scheduled_delivery ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                              In Transit
                            </span>
                          ) : (
                            new Date(lateLoad.actual_delivery).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold bg-gradient-to-r from-red-500 to-red-600 text-white shadow-sm">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          <span>{Math.abs(lateLoad.hours_late).toFixed(1)}h late</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">
                          {lateLoad.reason || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">
                          {new Date(lateLoad.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </td>
                      {canDeleteLateLoads && (
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleDeleteLateLoad(lateLoad.id, lateLoad.dispatch_number)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Delete late load record"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Detention Claims Tab */
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Explanation */}
          <div className="bg-orange-50 border-b border-orange-200 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-orange-900 mb-1">How Detention Claims Are Tracked</h3>
                <p className="text-xs text-orange-800">
                  Detention claims are recorded when a truck arrives at the delivery location and waits <strong>more than 2 hours</strong> to be unloaded. 
                  The system tracks detention time from arrival until departure/completion. 
                  This is used for billing detention charges to customers for excessive wait times.
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Load ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Driver / Truck
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Arrival Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Departure Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Detention Hours
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Claim Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {detentionClaims.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-slate-500">
                      <div className="flex flex-col items-center">
                        <Clock className="w-12 h-12 text-slate-300 mb-3" />
                        <p className="font-semibold text-slate-700">No Detention Claims</p>
                        <p className="text-sm text-slate-500 mt-1">No loads with detention time over 2 hours</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  detentionClaims.map((claim) => (
                    <tr key={claim.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-semibold text-sm text-slate-900">{claim.dispatch_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-slate-900">{claim.driver_name}</p>
                          <p className="text-xs text-slate-500">{claim.truck_number}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                            claim.location_type === 'delivery' 
                              ? 'bg-green-100 text-green-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {claim.location_type === 'delivery' ? '📦 Delivery' : '📍 Pickup'}
                          </span>
                          <p className="text-xs text-slate-600">{claim.location_address}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-900">
                          {new Date(claim.arrival_time).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-900">
                          {claim.departure_time 
                            ? new Date(claim.departure_time).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })
                            : 'Still waiting'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                          {claim.detention_hours.toFixed(1)}h
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-slate-900">
                          ${claim.total_claim_amount.toFixed(2)}
                        </span>
                        <p className="text-xs text-slate-500">${claim.detention_rate}/hr</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          claim.claim_status === 'paid' 
                            ? 'bg-green-100 text-green-700'
                            : claim.claim_status === 'approved'
                            ? 'bg-blue-100 text-blue-700'
                            : claim.claim_status === 'filed'
                            ? 'bg-yellow-100 text-yellow-700'
                            : claim.claim_status === 'denied'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          {claim.claim_status.charAt(0).toUpperCase() + claim.claim_status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">
                          {claim.notes || '-'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
